import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  TrendingUp, Clock, AlertTriangle, Percent,
  Users, FileText, Receipt, CalendarDays, CheckSquare, ClipboardList,
  UserPlus, ShoppingCart, CreditCard, FileX, Pencil, Loader2,
} from "lucide-react";
import { dashboardAPI, billingAPI, taskAPI } from "@/lib/api";
import ContractForm from "@/components/ContractForm";
import { InvoicePreviewSendDialog } from "@/components/billing/InvoicePreviewSendDialog";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";

function formatEur(val) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(val || 0);
}

function formatDateFR(d) {
  if (!d) return "-";
  return new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export default function TenantDashboard({ user }) {
  const [data, setData] = useState(null);
  const [billingForecast, setBillingForecast] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("12m");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customRange, setCustomRange] = useState(false);
  const [generating, setGenerating] = useState({});
  const [editContractId, setEditContractId] = useState(null);
  const [editClientId, setEditClientId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const navigate = useNavigate();
  const hasContrats = (user?.modules || []).includes("contrats");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (customRange && dateFrom) params.date_from = dateFrom;
    if (customRange && dateTo) params.date_to = dateTo;
    if (!customRange && period) params.period = period;
    Promise.all([
      dashboardAPI.get(params).then(r => setData(r.data)).catch(() => {}),
      billingAPI.forecast().then(r => setBillingForecast(r.data || [])).catch(() => {}),
      taskAPI.list().then(r => setTasks(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [period, dateFrom, dateTo, customRange]);

  useEffect(() => { load(); }, [load]);

  const setPeriodPreset = (val) => { setCustomRange(false); setPeriod(val); };
  const applyCustomRange = () => { if (dateFrom && dateTo) { setCustomRange(true); setPeriod(""); } };

  const generateInvoice = async (item) => {
    setGenerating(prev => ({ ...prev, [item.contract_id]: true }));
    try {
      const res = await billingAPI.generate({ contract_id: item.contract_id });
      toast.success(`Facture ${res.data.number} créée`);
      setPreviewDoc(res.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setGenerating(prev => ({ ...prev, [item.contract_id]: false }));
    }
  };

  const periodLabel = () => {
    if (customRange && dateFrom && dateTo) return `${new Date(dateFrom).toLocaleDateString("fr-FR")} - ${new Date(dateTo).toLocaleDateString("fr-FR")}`;
    if (period === "12m") return "12 derniers mois";
    if (period === "3m") return "3 derniers mois";
    if (period === "6m") return "6 derniers mois";
    if (period === "9m") return "9 derniers mois";
    if (period === "ytd") return "Depuis debut d'année";
    if (period.startsWith("year_")) return `Année ${period.replace("year_", "")}`;
    return "Toutes périodes";
  };

  const pendingTasks = useMemo(() => tasks.filter(t => !t.done), [tasks]);

  if (loading && !data) {
    return (
      <div className="page-container flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard-page">
      {/* Header + Period filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Tableau de bord
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{periodLabel()}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={customRange ? "" : period} onValueChange={setPeriodPreset} data-testid="period-select">
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="period-trigger">
              <CalendarDays size={13} className="mr-1.5 text-slate-400" />
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes périodes</SelectItem>
              <SelectItem value="12m">12 derniers mois</SelectItem>
              <SelectItem value="3m">3 derniers mois</SelectItem>
              <SelectItem value="6m">6 derniers mois</SelectItem>
              <SelectItem value="9m">9 derniers mois</SelectItem>
              <SelectItem value="ytd">Depuis debut d'année</SelectItem>
              {(data?.available_years || []).map(y => (
                <SelectItem key={y} value={`year_${y}`}>Année {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <DateField value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs w-32" data-testid="date-from" />
            <span className="text-xs text-slate-400">a</span>
            <DateField value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs w-32" data-testid="date-to" />
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={applyCustomRange} disabled={!dateFrom || !dateTo} data-testid="apply-range-btn">OK</Button>
          </div>
        </div>
      </div>

      {/* ===== LIGNE 1 : KPIs financiers ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-row">
        <KpiCard
          testid="kpi-ca-ht"
          icon={TrendingUp} iconColor="text-emerald-600" iconBg="bg-emerald-50"
          label="CA HT" value={formatEur(data?.revenue_ht)}
          onClick={() => navigate("/documents?type=facture&status=payee")}
        />
        <KpiCard
          testid="kpi-pending"
          icon={Clock} iconColor="text-orange-600" iconBg="bg-orange-50"
          label="En attente de paiement" value={formatEur(data?.pending_ht)}
          onClick={() => navigate("/documents?type=facture&status=envoyee")}
        />
        <KpiCard
          testid="kpi-overdue"
          icon={AlertTriangle} iconColor="text-red-600" iconBg="bg-red-50"
          label="Solde impaye" value={formatEur(data?.overdue_ht)}
          sub={data?.overdue_ht > 0 ? "Échéance dépassée" : null}
          subColor="text-red-500"
        />
        <KpiCard
          testid="kpi-recovery"
          icon={Percent} iconColor="text-indigo-600" iconBg="bg-indigo-50"
          label="Taux de recouvrement" value={`${data?.recovery_rate ?? 0} %`}
          sub={(() => {
            const rate = data?.recovery_rate ?? 0;
            if (rate >= 80) return "Bon";
            if (rate >= 50) return "Moyen";
            return "A améliorer";
          })()}
          subColor={(() => {
            const rate = data?.recovery_rate ?? 0;
            if (rate >= 80) return "text-emerald-500";
            if (rate >= 50) return "text-amber-500";
            return "text-red-500";
          })()}
        />
      </div>

      {/* ===== LIGNE 2 : Compteurs compacts ===== */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="counters-row">
        <CounterBadge icon={Users} label="Clients" count={data?.total_clients || 0} onClick={() => navigate("/clients?type=client")} testid="counter-clients" />
        <CounterBadge icon={UserPlus} label="Prospects" count={data?.total_prospects || 0} onClick={() => navigate("/clients?type=prospect")} testid="counter-prospects" />
        <CounterBadge icon={FileText} label="Factures" count={data?.doc_counts?.facture || 0} onClick={() => navigate("/documents?type=facture")} testid="counter-factures" />
        <CounterBadge icon={CreditCard} label="Devis" count={data?.doc_counts?.devis || 0} onClick={() => navigate("/documents?type=devis")} testid="counter-devis" />
        <CounterBadge icon={ShoppingCart} label="Commandes" count={data?.doc_counts?.commande || 0} onClick={() => navigate("/documents?type=commande")} testid="counter-commandes" />
        <CounterBadge icon={FileX} label="Avoirs" count={data?.doc_counts?.avoir || 0} onClick={() => navigate("/documents?type=avoir")} testid="counter-avoirs" />
      </div>

      {/* ===== LIGNE 3 : Factures à établir (contrats) ===== */}
      {hasContrats && billingForecast.length > 0 && (
        <Card className="p-5 border border-slate-200 rounded-lg bg-white mb-6" data-testid="billing-forecast-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Receipt size={16} className="text-violet-600" /> Factures à établir
            </h3>
            <button onClick={() => navigate("/facturation")} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 uppercase tracking-wider" data-testid="view-billing-btn">
              Voir tout
            </button>
          </div>
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="uppercase text-[11px] text-slate-500 font-medium">Client</TableHead>
                  <TableHead className="uppercase text-[11px] text-slate-500 font-medium">Produit</TableHead>
                  <TableHead className="uppercase text-[11px] text-slate-500 font-medium">Date facturation</TableHead>
                  <TableHead className="uppercase text-[11px] text-slate-500 font-medium text-right">Montant HT</TableHead>
                  <TableHead className="uppercase text-[11px] text-slate-500 font-medium w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billingForecast.slice(0, 10).map((item) => {
                  const isPast = item.date < new Date().toISOString().split("T")[0];
                  return (
                    <TableRow key={item.contract_id || `${item.client_id}-${item.date}`} className={isPast ? 'bg-red-50/50' : ''}>
                      <TableCell>
                        <button onClick={() => navigate(`/clients/${item.client_id}`)} className="text-sm text-indigo-600 hover:underline font-medium" data-testid={`billing-client-${item.contract_id}`}>
                          {item.client_name}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{item.product_name}{item.label ? ` — ${item.label}` : ""}</TableCell>
                      <TableCell>
                        <span className="text-sm">{formatDateFR(item.date)}</span>
                        {isPast && <Badge variant="destructive" className="text-[10px] px-1.5 py-0 ml-2">Retard</Badge>}
                      </TableCell>
                      <TableCell className="text-right font-medium text-sm money-value">{formatEur(item.client_price)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Modifier le contrat" onClick={() => { setEditContractId(item.contract_id); setEditClientId(item.client_id); }} data-testid={`billing-edit-${item.contract_id}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => generateInvoice(item)}
                            disabled={generating[item.contract_id]}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7"
                            data-testid={`billing-generate-${item.contract_id}`}
                          >
                            {generating[item.contract_id] ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Receipt size={14} className="mr-1" />}
                            Facturer
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {billingForecast.length > 10 && (
            <p className="text-xs text-slate-400 mt-2 text-center">
              et {billingForecast.length - 10} autre{billingForecast.length - 10 > 1 ? "s" : ""}...{" "}
              <button onClick={() => navigate("/facturation")} className="text-indigo-600 hover:underline">Voir tout</button>
            </p>
          )}
        </Card>
      )}

      {/* ===== LIGNE 3 : Watchlist + Taches ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-testid="watchlist-tasks-row">
        {/* Factures a surveiller */}
        <Card className="p-5 border border-slate-200 rounded-lg bg-white" data-testid="watchlist-section">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <AlertTriangle size={16} className="text-orange-500" /> Factures a surveiller
          </h3>
          <div className="space-y-0 max-h-[380px] overflow-y-auto">
            {(data?.watchlist || []).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">Aucune facture en attente</p>
            )}
            {(data?.watchlist || []).slice(0, 20).map((inv) => (
              <WatchlistRow key={inv.id} inv={inv} navigate={navigate} />
            ))}
            {(data?.watchlist || []).length > 20 && (
              <p className="text-xs text-slate-400 text-center pt-2">
                et {data.watchlist.length - 20} autre{data.watchlist.length - 20 > 1 ? "s" : ""}...{" "}
                <button onClick={() => navigate("/documents?type=facture&status=envoyee")} className="text-indigo-600 hover:underline">Voir tout</button>
              </p>
            )}
          </div>
        </Card>

        {/* Taches */}
        <Card className="p-5 border border-slate-200 rounded-lg bg-white" data-testid="tasks-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CheckSquare size={16} className="text-indigo-500" /> Taches
              {pendingTasks.length > 0 && (
                <Badge variant="secondary" className="text-[10px] ml-1">{pendingTasks.length}</Badge>
              )}
            </h3>
          </div>
          <div className="space-y-0 max-h-[380px] overflow-y-auto">
            {pendingTasks.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <ClipboardList size={28} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Aucune tache en cours</p>
              </div>
            )}
            {pendingTasks.slice(0, 15).map((task) => (
              <TaskRow key={task.id} task={task} navigate={navigate} />
            ))}
          </div>
        </Card>
      </div>

      <ContractForm
        open={!!editContractId}
        onOpenChange={(v) => { if (!v) { setEditContractId(null); setEditClientId(null); } }}
        clientId={editClientId}
        contractId={editContractId}
        onSaved={load}
      />

      <InvoicePreviewSendDialog
        open={!!previewDoc}
        onOpenChange={(v) => { if (!v) setPreviewDoc(null); }}
        doc={previewDoc}
        onSent={load}
      />
    </div>
  );
}

/* ---- Sub-components ---- */

function KpiCard({ testid, icon: Icon, iconColor, iconBg, label, value, sub, subColor, onClick }) {
  return (
    <Card
      className={`p-5 border border-slate-200 rounded-lg bg-white ${onClick ? "cursor-pointer hover:shadow-md hover:border-slate-300 transition-all" : ""}`}
      onClick={onClick}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon size={20} className={iconColor} strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 mb-0.5">{label}</p>
          <p className="text-lg font-bold text-slate-900 money-value leading-tight">{value}</p>
          {sub && <p className={`text-[11px] mt-0.5 ${subColor || "text-slate-400"}`}>{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function WatchlistRow({ inv, navigate }) {
  const colorMap = { red: "bg-red-500", orange: "bg-orange-400" };
  const colorDot = colorMap[inv.color] || "bg-slate-300";
  const rowBg = inv.color === "red" ? "bg-red-50/40" : "";
  return (
    <div className={`flex items-center gap-3 py-2.5 px-2 border-b border-slate-100 last:border-0 rounded ${rowBg}`} data-testid={`watchlist-${inv.id}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${colorDot}`} />
      <div className="flex-1 min-w-0">
        <button onClick={() => navigate(`/documents?type=facture`)} className="text-sm font-medium text-slate-800 hover:text-indigo-600 truncate block text-left">
          {inv.number}
        </button>
        <p className="text-xs text-slate-500 truncate">{inv.client_name}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-semibold text-slate-900 money-value">{formatEur(inv.total_ht)}</p>
        <p className={`text-[11px] ${{ red: "text-red-500 font-medium", orange: "text-orange-500" }[inv.color] || "text-slate-400"}`}>
          {inv.due_date ? formatDateFR(inv.due_date) : "Sans échéance"}
        </p>
      </div>
    </div>
  );
}

function TaskRow({ task, navigate }) {
  const dueInfo = task.due_date ? getDueInfo(task.due_date) : null;
  const priorityDot = task.priority === "haute" ? "bg-red-500" : task.priority === "basse" ? "bg-sky-400" : "bg-slate-400";
  return (
    <div className="flex items-start gap-2.5 py-2.5 px-2 border-b border-slate-100 last:border-0" data-testid={`dashboard-task-${task.id}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${priorityDot}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 leading-snug truncate">{task.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {dueInfo && <span className={`text-[11px] ${dueInfo.color}`}>{dueInfo.text}</span>}
          {task.client_name && task.client_id && (
            <button onClick={() => navigate(`/clients/${task.client_id}`)} className="text-[11px] text-indigo-500 hover:underline truncate">
              {task.client_name}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function getDueInfo(d) {
  const date = new Date(d + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((date - today) / 86400000);
  const formatted = date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  if (diff < 0) return { text: formatted, color: "text-red-500" };
  if (diff === 0) return { text: "Aujourd'hui", color: "text-amber-600" };
  if (diff === 1) return { text: "Demain", color: "text-amber-500" };
  return { text: formatted, color: "text-slate-400" };
}

function CounterBadge({ icon: Icon, label, count, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all"
      data-testid={testid}
    >
      <Icon size={15} className="text-slate-500" strokeWidth={1.5} />
      <span className="text-sm text-slate-600">{label}</span>
      <Badge variant="secondary" className="text-xs font-bold ml-0.5">{count}</Badge>
    </button>
  );
}
